import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useRef, useState} from 'react';
import {objectArrayDiff} from 'utils/functions/ArrayHelpers';
import {Highlight} from 'utils/hooks/useDraftAsTextHighlighter/useDraftAsTextHighlighter';
import {Button} from 'letrus-ui';
import styles from './HighlightTab.module.scss';

export interface HighlightTabProps {
  deleteHighlight(highlight: Highlight): void;
  highlightList?: Highlight[];
}

function HighlightTab({
  deleteHighlight,
  highlightList,
}: HighlightTabProps): JSX.Element {
  const highlightListRef = useRef<HTMLUListElement>(null);
  const highlightListElement = highlightListRef.current;
  const [previousHighlightList, setPreviousHighlightList] =
    useState<Highlight[]>();

  useEffect(() => {
    const lastHighlight = highlightList?.slice(-1)[0];
    const lastAddedHighlight = objectArrayDiff<Highlight>(
      'text',
      highlightList,
      previousHighlightList,
    );

    closeOtherHighlights(lastAddedHighlight?.[0] ?? lastHighlight);
    setPreviousHighlightList(highlightList);
  }, [highlightList, highlightListElement]);

  function closeOtherHighlights(highlight?: Highlight) {
    if (highlightListElement) {
      const highlightNodes = Array.from(highlightListElement.children);

      highlightNodes.forEach((highlightNode, index) => {
        if (index !== highlight?.index) {
          highlightNode.children[0].removeAttribute('open');
        }
      });

      const highlightListScale =
        highlightListElement.scrollHeight / highlightNodes.length;

      highlightListElement.scrollTop =
        highlightListScale * Number(highlight?.index);
    }
  }

  return (
    <>
      <header className={styles.highlightTabHeader}>
        <h2>Suas marcações:</h2>
        <p>Aqui você vai encontrar todas suas marcações.</p>
      </header>

      <ul className={styles.highlightsList} ref={highlightListRef}>
        {highlightList?.map((highlight, index) => (
          <li key={highlight.index}>
            <details className={styles.highlightCard} open>
              <summary
                className={styles.title}
                onClick={() => closeOtherHighlights(highlight)}
              >
                <h3>{`${index + 1}ª Marcação`}</h3>
                <FontAwesomeIcon icon="chevron-down" />
              </summary>

              <article className={styles.innerCard}>
                <p className={styles.title}>Você grifou:</p>

                <p
                  dangerouslySetInnerHTML={{
                    __html: highlight.withoutNestedMarks,
                  }}
                />
              </article>

              <Button
                rounded
                variant="secondary"
                aria-label="Excluir marcação"
                onClick={() => deleteHighlight(highlight)}
              >
                <FontAwesomeIcon icon="trash-alt" />
              </Button>
            </details>
          </li>
        ))}

        {!highlightList && (
          <li>
            <span className={styles.emptyListCard}>
              <p>
                Sem marcações <span className={styles.emoji}>🥲</span>
              </p>
              <p>
                {`Para criar uma marcação, clique no botão 
                "Grifar" acima do texto motivador e selecione
                as linhas no texto ao lado.`}
              </p>
            </span>
          </li>
        )}
      </ul>
    </>
  );
}

export default HighlightTab;
